import React from 'react';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';
import styled, { css, useTheme } from 'styled-components/macro';

// components
import { ModalVideoCTA } from 'components/modals/ModalVideoCTA';
import { ModalChapters } from 'components/modals/ModalChapters';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { OnScreenPlayButton, VideoControls } from './components';
// types
import {
  InteractiveCTAOption,
  VideoChapterItem,
  VideoChapterItemExtended,
} from 'types';
// utils
import { addCallToAction } from 'utils/helpers';
import { formatTimeToString } from 'utils/date';
// hooks
import { useContainerDimensions } from 'hooks';
// queries
import { useUpdateStatsHotspotMutation } from 'queries/stats/updateStatsHotspotMutation';
import { useUpdateViewMutation } from 'queries/view/updateViewMutation';
// assets
import { useParams } from 'react-router';
import { AnnotationItem } from '../../queries/details/types';
import * as Sentry from '@sentry/react';
import { IoMdLink } from 'react-icons/io';
import { useTranslation } from 'react-i18next';
import { useGetTranslationInfoQuery } from 'queries/video/useGetTranslationInfoQuery';
import { CAPTION, CAPTION_HEADER, PLACEHOLDER } from 'utils/constants';
import { TranscriptionData } from 'queries/video/types';

type Props = {
  videoSource: string;
  hidePoweredBy: boolean;

  playerBackgroundColor?: string;
  playerIconsColor?: string;
  playButtonPosition?: string;
  thumbnail?: string;
  videoId?: number;
  userId?: number;
  videoRef?: any;
  small?: boolean;
  autoplay?: boolean;
  onPlay?: Function;
  playlist?: any[];
  preventWideSize?: boolean;
  selectedAnnotationId?: string | undefined;
  previewAnnotation?: any;
  hideContext?: boolean;
  startWithControls?: boolean;
  height?: string;
  width?: string;
  margin?: string;
  interactiveCTAs?: InteractiveCTAOption[];
  isPreview?: boolean;
  isAutomotive?: boolean;
  videoChapters?: VideoChapterItem[];
  setAreControlsShown?: (arg: boolean) => void;
  showContactInfoModal?: boolean;
  openShowContactInfoModal?: () => void;
  transcript?: TranscriptionData;
};

type PlayerLayoutProps = {
  height?: string;
  width?: string;
  small?: boolean;
  margin?: string;
};
type ControlProps = {
  hex: any;
  small?: boolean;
  isVisible: boolean;
};
type FigureProps = {
  small?: boolean;
  preventWideSize?: boolean;
  maxWidth?: number;
};
type ChapterProps = {
  isFullScreen?: boolean;
  showChaptersModal?: boolean;
};

interface CustomTextTrackCueList extends TextTrackCueList {
  line: number;
}

interface CustomHTMLTrackElement extends HTMLTrackElement {
  // Add the activeCues property
  activeCues: CustomTextTrackCueList[];
}

const PlayerLayout = styled.div<PlayerLayoutProps>`
  box-sizing: border-box;
  overflow: overlay;
  display: flex;
  justify-content: center;
  width: 100%;
  margin: 0 0px 20px;
  min-width: 384px;
  @media screen and (max-width: 520px) {
    min-width: auto !important;
  }

  @media screen and (min-width: 2800px) {
    width: 800px;
    height: 420px;
  }
`;

const Figure = styled.figure<FigureProps>`
  border-radius: 7px;
  margin: 0;
  position: relative;
  height: 100%;
  max-width: ${props => (props.maxWidth ? props.maxWidth + 'px' : '620px')};
  overflow: hidden;
  width: 100%;
  @media screen and (max-width: 520px) {
    width: 100% !important;
    height: auto !important;
    min-width: auto !important;
  }

  @media screen and (min-width: 2800px) {
    max-width: unset;
  }
`;

const FullscreenWrapper = styled(FullScreen)`
  width: 100%;
  position: relative;
  box-sizing: border-box;
`;

const Video = styled.video<any>`
  display: block;
  border-radius: 7px;
  position: relative;
  width: 100%;
  height: ${props => (props.isFullScreen ? '100%' : props.height)};
  object-fit: contain;
  max-height: ${props => (props.isFullScreen ? '100%' : '100%')};
  background: rgba(0, 0, 0, 0.5);

  @media screen and (min-width: 2800px) {
    height: ${props => (props.isFullScreen ? '100%' : '420px')};
  }
`;

const Overlay = styled.div`
  display: block;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: ${props => props.theme.colors.white[80]};
  z-index: 4;
`;

const Controls = styled.div<ControlProps>`
  transition: all 0.15s;
  position: absolute;
  bottom: 0;
  opacity: ${props => (props.isVisible ? 1 : 0)};
  left: 0;
  right: 0;
  background-color: ${props => props.hex};
  padding: ${props => `${props.small ? '0px 2px 1px' : '8px'}`};
`;

const Annotation = styled.div<any>`
  opacity: ${props => (props.show ? '1' : '0')};
  box-sizing: border-box;
  display: flex;
  align-items: center;
  position: absolute;
  border-radius: 5px;
  transition: opacity 0.3s ease;
  z-index: ${props => (props.show ? 8 : 0)};
`;

const AnnotationImageWrapper = styled.div<any>`
  width: 100%;
  height: calc(100% - ${({ hasControls }) => (hasControls ? '45px' : '0px')});
  position: absolute;
  top: 0;
`;
const AnnotationImage = styled(Annotation)<any>`
  width: ${props =>
    `${
      props.imageWidthPercentage
        ? props.imageWidthPercentage
        : props.calcImageWidth
    }`};
  height: ${props =>
    `${
      props.imageWidthPercentage
        ? props.imageWidthPercentage
        : props.calcImageHeight
    }`};
  opacity: ${props => (props.show ? props.imageOpacity : '0')};
  display: inline-block;
  background-image: url('${props => props.src}');
  background-repeat: no-repeat;
  background-size: contain;
  border-radius: 0px;

  ${props =>
    props.position === 'Top Left' &&
    css`
      top: 15px;
      left: 5px;
      background-position: top left;
    `}
  ${props =>
    props.position === 'Top Center' &&
    css`
      top: 15px;
      left: 50%;
      transform: translate(-50%, 0);
      background-position: top center;
    `}
  ${props =>
    props.position === 'Center' &&
    css`
      left: 50%;
      top: ${props.startWithControls ? 'calc(50% + 5px)' : '50%'};
      transform: translate(-50%, -50%);
      background-position: center;
    `}
  ${props =>
    props.position === 'Top Right' &&
    css`
      top: 15px;
      right: 5px;
      background-position: top right;
    `}
  ${props =>
    props.position === 'Bottom Left' &&
    css`
      bottom: ${props.startWithControls ? '5px' : '15px'};
      left: 5px;
      background-position: bottom left;
    `}
  ${props =>
    props.position === 'Bottom Center' &&
    css`
      bottom: ${props.startWithControls ? '5px' : '15px'};
      left: 50%;
      transform: translate(-50%, 0);
      background-position: bottom center;
    `}
  ${props =>
    props.position === 'Bottom Right' &&
    css`
      bottom: ${props.startWithControls ? '5px' : '15px'};
      right: 5px;
      background-position: bottom right;
    `}
`;
const AnnotationText = styled(Annotation)<any>`
  padding: ${props => props.padding || '12px 16px'};
  z-index: 9;
  font-size: ${props => props.fontSize}px;
  font-family: ${props => props.fontFamily};
  color: ${props => props.textColor};
  background: ${props => props.backgroundColor};
  border-color: ${props => props.borderColor};
  border-width: ${props => props.borderWidth}px;
  border-style: ${props => (props.borderWidth ? 'solid' : '')};
  text-decoration: none;
  outline: none;
  white-space: pre-wrap;
  ${props => (props.textBold ? `font-weight: 700;` : '')}
  ${props => (props.textItalic ? `font-style: italic;` : '')}
  ${props => (props.textUnderline ? `text-decoration: underline;` : '')}

  &:active {
    color: ${props => props.textColor};
  }

  .formatText {
    inline-size: 430px;
    overflow-wrap: break-word;
    padding: 15px;
  }

  ${props => props.isLinkEnabled && 'cursor: pointer;'}
  ${props =>
    props.position === 'Top Left' &&
    css`
      top: 12px;
      left: 0px;
    `}
  ${props =>
    props.position === 'Top Center' &&
    css`
      top: 12px;
      left: 50%;
      transform: translate(-50%, 0);
    `}
  ${props =>
    props.position === 'Center' &&
    css`
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
    `}
  ${props =>
    props.position === 'Top Right' &&
    css`
      top: 12px;
      right: 0px;
    `}
  ${props =>
    props.position === 'Bottom Left' &&
    css`
      bottom: ${props.startWithControls ? '46px' : '12px'};
      left: 0px;

      @media screen and (min-width: 2800px) {
        bottom: ${props.startWithControls ? '55px' : '15px'};
      }
    `}
  ${props =>
    props.position === 'Bottom Center' &&
    css`
      bottom: ${props.startWithControls ? '46px' : '12px'};
      left: 50%;
      transform: translate(-50%, 0);

      @media screen and (min-width: 2800px) {
        bottom: ${props.startWithControls ? '55px' : '15px'};
      }
    `}
  ${props =>
    props.position === 'Bottom Right' &&
    css`
      bottom: ${props.startWithControls ? '46px' : '12px'};
      right: 0px;

      @media screen and (min-width: 2800px) {
        bottom: ${props.startWithControls ? '55px' : '15px'};
      }
    `}

    ${props =>
    props.bottom &&
    css`
      bottom: ${props.startWithControls
        ? `calc(${props.bottom} + 46px)`
        : `calc(${props.bottom} + 15px)`};

      @media screen and (min-width: 2800px) {
        bottom: ${props.startWithControls
          ? `calc(${props.bottom} + 55px)`
          : `calc(${props.bottom} + 20px)`};
      }
    `}
`;
const LinkWrapper = styled.div`
  margin-right: 5px;
  margin-top: 2px;
`;
const ChaptersPreviewButton = styled.button<ChapterProps>`
  box-sizing: border-box;
  position: absolute;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  font-weight: 600;
  font-size: 13px;
  line-height: 24px;
  bottom: 56px;
  right: 8px;
  height: 32px;
  border-radius: 6px;
  color: ${props => props.theme.colors.secondary[100]};
  background-color: ${props => props.theme.colors.white[100]};
  border: 1px solid rgba(0, 27, 83, 0.05);
  cursor: pointer;
  padding: 4px 12px;

  @media screen and (max-width: 640px) {
    display: ${props => (props.showChaptersModal ? 'none' : '')};
  }
`;
const Text = styled.div`
  display: flex;
  align-items: center;
  line-height: 1.5;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  justify-content: center;
`;
const IconWrapper = styled.div`
  display: flex;
  width: auto;
  color: ${props => props.theme.colors.secondary[20]};
  font-size: 24px;
`;

const RETRY_DELAY = 1000; //ms

const openNewTab = (url: string, type: string) =>
  window.open(url, type, 'noopener,noreferrer');

const CAPTIONS_CACHE: Record<string, string> = {};

const getCaptionFromCache = (key: string): string | undefined => {
  const normalizedKey = key.trim().toLowerCase();
  return CAPTIONS_CACHE[normalizedKey];
};

const setCaptionInCache = (key: string, value: string) => {
  const normalizedKey = key.trim().toLowerCase();
  CAPTIONS_CACHE[normalizedKey] = value;
};

export const VideoPlayer = (props: Props) => {
  const {
    playerBackgroundColor,
    videoSource,
    playButtonPosition,
    thumbnail,
    playerIconsColor,
    small,
    videoRef,
    autoplay,
    onPlay,
    playlist,
    preventWideSize,
    selectedAnnotationId = '',
    hideContext = false,
    startWithControls = true,
    videoId,
    previewAnnotation,
    height,
    width,
    margin,
    userId,
    interactiveCTAs,
    isPreview = false,
    isAutomotive = false,
    videoChapters,
    setAreControlsShown,
    showContactInfoModal,
    openShowContactInfoModal,
    hidePoweredBy,
    transcript,
  } = props;
  const themes = useTheme();
  const { t } = useTranslation();
  const { shortUrl = '' } = useParams();
  const [playerBgColor, setPlayerBgColor] = React.useState(
    playerBackgroundColor || ''
  );
  const [playBtnPosition, setPlayBtnPosition] = React.useState(
    playButtonPosition || ''
  );
  const [playerIcColor, setPlayerIcColor] = React.useState(
    playerIconsColor || ''
  );
  const [isVideoPaused, setIsVideoPaused] = React.useState(true);
  const [isVideoMuted, setIsVideoMuted] = React.useState(false);
  const [volumeRange, setVolumeRange] = React.useState(1);
  const [duration, setDuration] = React.useState(0);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [hasControls, setHasControls] = React.useState(startWithControls);
  const [isError, setIsError] = React.useState(false);
  const [retryTimeout, setRetryTimeout] = React.useState(0);
  const [hotspotData, setHotSpotData] = React.useState([]);
  const [isHotSpotInitialized, setInitialized] = React.useState(false);
  const [isVideoViewed, setIsVideoViewed] = React.useState(false);
  const [lastTime, setLastTime] = React.useState(0);
  const [videoChaptersData, setVideoChaptersData] = React.useState<
    VideoChapterItemExtended[]
  >([]);
  const [showChaptersModal, setShowChaptersModal] =
    React.useState<boolean>(false);
  const isVideoPlayedRef = React.useRef<boolean | null>(null);
  const [isVideoFinished, setIsVideoFinished] = React.useState(false);
  const [showCtaVideoModal, setShowCtaVideoModal] = React.useState('');
  const [selectedLanguage, setSelectedLanguage] = React.useState<string>('en');

  const { mutateAsync: updateViewMutation } = useUpdateViewMutation();

  const { data: translationData } = useGetTranslationInfoQuery({
    videoId: videoId || 0,
    userId: Number(transcript?.userId) || 0,
    language: selectedLanguage,
    enabled:
      !!videoId &&
      !!transcript?.userId &&
      transcript?.status === 'finished' &&
      !!transcript?.enabled &&
      !!selectedLanguage &&
      selectedLanguage !== 'off' &&
      selectedLanguage !== 'auto',
  });

  const normalizeVtt = (text: string) => {
    const captionHeader = CAPTION_HEADER;
    const placeholder = PLACEHOLDER;

    let result = text;

    if (result.substring(0, 11) === 'वेबवीटीटी\n\n') {
      result = `${CAPTION}\n\n` + result.substring(11);
    }

    return result
      .replaceAll(placeholder, captionHeader)
      .replaceAll(' .', '.')
      .replaceAll('—>', '-->');
  };

  const translatedCaptions = React.useMemo(() => {
    if (!translationData?.translatedText || selectedLanguage === 'off') {
      return '';
    }
    const raw = translationData.translatedText;

    const cached = getCaptionFromCache(selectedLanguage);
    if (cached) {
      return cached;
    }

    const normalized = normalizeVtt(raw);

    setCaptionInCache(selectedLanguage, normalized);

    return normalized;
  }, [translationData?.translatedText, selectedLanguage]);

  const autoCaptions = React.useMemo(() => {
    if (!videoId || !transcript) {
      return '';
    }

    const auto =
      transcript?.modifiedTranscript || transcript?.originalTranscript || '';

    setCaptionInCache('auto', auto);

    return auto;
  }, [videoId, transcript]);

  const captions = React.useMemo(() => {
    if (selectedLanguage === 'off') {
      return '';
    }

    if (selectedLanguage === 'auto') {
      return autoCaptions;
    }

    return translatedCaptions;
  }, [selectedLanguage, autoCaptions, translatedCaptions]);

  const captionsEnabled = React.useMemo(() => {
    if (!transcript) {
      return false;
    }

    const enabled =
      typeof transcript?.enabled === 'string'
        ? Number(transcript?.enabled)
        : transcript?.enabled;

    return !!enabled;
  }, [transcript]);

  const initialCaptionsLoaded = captions;

  const POST_INTERVAL = 3;

  const hasControlsRef = React.useRef(hasControls);

  let playlistIndex = React.useRef(0);

  const [retryCount, setRetryCount] = React.useState(0);
  const controlsRef = React.createRef<HTMLDivElement>();

  let videoAnnotations: any = [];
  videoAnnotations = previewAnnotation?.length
    ? previewAnnotation
    : [previewAnnotation];

  const { width: playerWidth, height: playerHeight } =
    useContainerDimensions(videoRef);

  const { mutateAsync: updateStatsHotspotMutation } =
    useUpdateStatsHotspotMutation();

  React.useEffect(() => {
    setPlayerBgColor(playerBackgroundColor || '');
    setPlayerIcColor(playerIconsColor || '');
    setPlayBtnPosition(playButtonPosition || '');
  }, [playerIconsColor, playButtonPosition, playerBackgroundColor]);

  React.useEffect(() => {
    const { current: video } = videoRef;

    const cueChangeHandler = () => {
      const line = hasControlsRef.current ? -4 : -1;
      if (video.textTracks[0].activeCues[0]) {
        video.textTracks[0].activeCues[0].line = line;
      }

      let cues = video?.textTracks?.[0]?.cues || []; // array of current cues
      for (var i = 0; i < cues.length; i++) {
        cues[i].line = line;
      }
    };

    if (video) {
      const file: File = new File([new Blob([captions])], 'subtitle.vtt', {
        type: 'text/vtt',
      });

      const url = window.URL.createObjectURL(file);
      if (video.textTracks.length > 0) {
        video.removeChild(video.children[1]);
      }
      const track = document.createElement('track');
      track.kind = 'captions';
      track.label = 'English';
      track.srclang = 'en';
      track.default = true;
      track.src = url;

      track.addEventListener('cuechange', function () {
        try {
          (track as CustomHTMLTrackElement).activeCues[0].line = hasControls
            ? -4
            : -1;
        } catch (e) {}
      });

      video.appendChild(track);
      video.textTracks[0].mode = 'showing';

      video.textTracks[0].addEventListener('cuechange', cueChangeHandler);
    }

    return () => {
      if (video && video.textTracks && video.textTracks[0]) {
        video.textTracks[0].removeEventListener('cuechange', cueChangeHandler);
      }
    };
  }, [captions]);

  const togglePlay = () => {
    try {
      const currentVideo = videoRef.current;
      isVideoPlayedRef.current = true;
      if (currentVideo) {
        isVideoPaused ? currentVideo.play() : currentVideo.pause();
      }
    } catch (error) {
      console.log('Failed to toggle play video', error);
    }
  };

  const contextMenu = (e: any) => {
    if (hideContext) e.preventDefault();
  };

  const fullScreenHandle = useFullScreenHandle();
  const toggleFullScreen = () => {
    const isIOS = /iPhone|iPad|iPod/i.test(
      navigator.userAgent || navigator.vendor
    );
    if (isIOS) {
      return togglePlay();
    }
    if (fullScreenHandle.active) {
      return fullScreenHandle.exit();
    }
    return fullScreenHandle.enter();
  };

  React.useEffect(() => {
    setTimeout(() => window.dispatchEvent(new Event('resize')), 200);
  }, [fullScreenHandle.active]);

  React.useEffect(() => {
    const video = videoRef.current;
    if (video.textTracks.length && video.textTracks[0]) {
      const line = hasControls ? -4 : -1;

      let cues = video?.textTracks?.[0]?.cues || []; // array of current cues
      for (var i = 0; i < cues.length; i++) {
        cues[i].line = line;
      }
      if (video.textTracks[0].activeCues[0]) {
        video.textTracks[0].activeCues[0].line = line;
      }
    }
    if (setAreControlsShown) setAreControlsShown(hasControls);
  }, [hasControls]);

  const handleMute = () => {
    const currentVideo = videoRef.current;
    if (currentVideo) {
      if (isVideoMuted) {
        setVolumeRange(1);
        setIsVideoMuted(false);
        currentVideo.volume = 1;
      } else {
        setVolumeRange(0);
        setIsVideoMuted(true);
        currentVideo.volume = 0;
      }
    }
  };

  const handleVolumeControl = (
    e: any,
    volumeRef: React.RefObject<HTMLInputElement>
  ) => {
    const volumeRefWidth = volumeRef.current && volumeRef.current.offsetWidth;
    let eventOffset = e.target.value; // e.nativeEvent.offsetX;

    // make sure that offset is never over 100 or under 0;
    if (eventOffset > 100) eventOffset = 100;
    if (eventOffset < 0) eventOffset = 0;

    if (videoRef.current && volumeRefWidth) {
      let volume = Number((eventOffset / volumeRefWidth).toFixed(2));

      if (
        Math.floor(videoRef.current.volume * 100) === e.target.valueAsNumber
      ) {
        return;
      }

      setVolumeRange(volume);

      if (volume < 0) {
        volume = 0;
        setVolumeRange(0);
      }
      if (volume > 1) {
        setVolumeRange(1);
        volume = 1;
      }

      if (volume === 0) setIsVideoMuted(true);
      if (volume !== 1) setIsVideoMuted(false);

      videoRef.current.volume = volume;
    }
  };

  const scrub = (
    e: any,
    progressRef: React.RefObject<HTMLProgressElement>,
    offset: number = 0,
    duration?: number
  ) => {
    const progressElWidth = progressRef.current
      ? progressRef.current.offsetWidth
      : 640;
    const durationValue = duration ? duration : videoRef.current.duration;
    if (videoRef.current) {
      const scrubTime =
        (e.nativeEvent.offsetX / progressElWidth) * durationValue + offset;
      // Check if scrubTime is finite before assigning it to currentTime
      if (!isFinite(scrubTime)) {
        Sentry.captureMessage('scrubTime is not a finite number', {
          extra: {
            scrubTime,
            offsetX: e.nativeEvent.offsetX,
            progressElWidth,
            durationValue,
            offset,
          },
        });
        return;
      }
      videoRef.current.currentTime = scrubTime;
    }
  };

  const handleDurationChange = () => {
    if (videoRef.current && isFinite(videoRef.current.duration)) {
      setDuration(videoRef.current.duration);
      if (videoChapters && videoChapters?.length > 0) {
        let videoChapterExtendedList: VideoChapterItemExtended[] = [];
        let lastPlaybackPosition = videoRef.current.duration;

        for (let i = videoChapters.length - 1; i >= 0; i--) {
          const progressRef = React.createRef<HTMLProgressElement>();
          const videoChapterDuration =
            lastPlaybackPosition - videoChapters[i].playbackPosition;
          const videoChapterPlaybackPosition =
            videoChapters[i].playbackPosition;
          const videoChapterMidPosition =
            videoChapterPlaybackPosition + videoChapterDuration / 2;
          let bubblePosition = Math.round(
            (videoChapterMidPosition / videoRef.current.duration) * 100
          );
          bubblePosition = bubblePosition > 90 ? 90 : bubblePosition;

          const newVideoChapter: VideoChapterItemExtended = {
            playbackPosition: videoChapterPlaybackPosition,
            title: videoChapters[i].title,
            ref: progressRef,
            duration: videoChapterDuration,
            bubblePosition: bubblePosition,
            playbackPositionText: formatTimeToString(
              videoChapterPlaybackPosition
            ),
          };
          videoChapterExtendedList.push(newVideoChapter);

          lastPlaybackPosition = videoChapterPlaybackPosition;
        }

        setVideoChaptersData([...videoChapterExtendedList.reverse()]);
      }
    }
  };

  const handleError = () => {
    if (
      videoRef.current &&
      videoRef.current.networkState === HTMLMediaElement.NETWORK_NO_SOURCE &&
      !isError
    ) {
      console.log('error loading mp4');
      setIsError(true);
    }
  };

  const handleLoadedMetadata = () => {
    setIsError(false);
    clearTimeout(retryTimeout);
  };

  React.useEffect(() => {
    if (isError) {
      setRetryCount(retryCount + 1);
    }
  }, [isError]);

  React.useEffect(() => {
    if (retryCount > 0 && isError) {
      clearTimeout(retryTimeout);
      const timeout: any = setTimeout(
        setRetryCount,
        RETRY_DELAY,
        retryCount + 1
      );
      setRetryTimeout(timeout);
    }
  }, [retryCount]);

  React.useEffect(() => {
    if (videoRef.current) {
      videoRef.current.addEventListener(
        'durationchange',
        handleDurationChange,
        true
      );
      videoRef.current.addEventListener(
        'loadedmetadata',
        handleLoadedMetadata,
        true
      );
      videoRef.current.addEventListener('error', handleError, true);
      videoRef.current.addEventListener(
        'pause',
        () => setIsVideoPaused(true),
        true
      );
      videoRef.current.addEventListener(
        'play',
        () => {
          setIsVideoFinished(false);
          setIsVideoPaused(false);
        },
        true
      );
      videoRef.current.addEventListener(
        'ended',
        () => {
          if (openShowContactInfoModal) openShowContactInfoModal();
          setIsVideoFinished(true);
        },
        true
      );

      if (controlsRef && controlsRef.current) {
        videoRef.current.addEventListener('mouseenter', showControls);
        videoRef.current.addEventListener('mouseleave', hideControls);
        controlsRef.current.addEventListener('mouseenter', showControls);
        controlsRef.current.addEventListener('mouseleave', hideControls);
      }
    }

    return () => {
      if (videoRef.current) {
        videoRef.current.removeEventListener(
          'durationchange',
          handleDurationChange,
          true
        );
        videoRef.current.removeEventListener(
          'loadedmetadata',
          handleLoadedMetadata,
          true
        );
        videoRef.current.removeEventListener('error', handleError, true);
      }
    };
  }, [videoRef]);

  const hideControls = () => {
    if (!videoRef.current) {
      return;
    }
    if (videoRef.current.paused) {
      return;
    }
    setHasControls(false);
    hasControlsRef.current = false;
  };

  const showControls = () => {
    setHasControls(true);
    hasControlsRef.current = true;
  };

  React.useEffect(() => {
    if (playlist && playlist.length > 0) {
      playlistIndex.current = 0;
      setCurrentTime(0);
      setIsVideoPaused(true);
    }
  }, [playlist]);

  React.useEffect(() => {
    const handleTimeUpdate = () => {
      if (videoRef.current) {
        setCurrentTime(videoRef.current.currentTime);
        if (playlistIndex && playlist && playlist[playlistIndex.current]) {
          if (
            videoRef.current.currentTime >=
            playlist[playlistIndex.current].endTime
          ) {
            const videoEndEvent = new Event('ended');
            videoRef.current.dispatchEvent(videoEndEvent);
          }
        }
      }
    };
    const handleVideoEnded = () => {
      if (videoRef.current && playlist) {
        playlistIndex.current++;
        if (
          playlistIndex.current < playlist.length &&
          playlist[playlistIndex.current].videoSource
        ) {
          videoRef.current.poster = playlist[playlistIndex.current].thumbnail;
          let time = '';
          const start = playlist[playlistIndex.current].startTime;
          const end = playlist[playlistIndex.current].endTime;
          if (start || end) {
            time = `#t=${start ? start : 0}${end ? ',' + end : ''}`;
          }
          let newSrc =
            ('' + playlist[playlistIndex.current].videoSource).split('#t=')[0] +
            time;
          videoRef.current.src = newSrc;
          videoRef.current.play();
        }
      }
    };

    if (videoRef.current) {
      videoRef.current.addEventListener('timeupdate', handleTimeUpdate, true);
    }
    if (videoRef.current) {
      videoRef.current.addEventListener('ended', handleVideoEnded, true);
    }
    if (videoRef.current && autoplay) {
      videoRef.current.play();
      setIsVideoPaused(false);
    }

    return () => {
      if (videoRef.current) {
        videoRef.current.removeEventListener(
          'timeupdate',
          handleTimeUpdate,
          true
        );
        videoRef.current.removeEventListener('ended', handleVideoEnded, true);
      }
    };
  }, [videoRef]);

  const setPlayBack = (rate: number) => {
    videoRef.current.playbackRate = rate;
  };

  const setTranslate = (language: string) => {
    if (!videoId) {
      return;
    }

    setSelectedLanguage(language);
  };

  const onChapterClick = (playbackPosition: number) => {
    videoRef.current.currentTime = playbackPosition;
    setShowChaptersModal(false);
  };

  const handleOnTimeUpdate = async () => {
    if (isPreview) {
      return;
    }
    if (!isHotSpotInitialized) {
      // initialize the array
      let hotspot: any = [];
      for (let i = 0; i < parseInt(String(duration)); i++) {
        hotspot[i] = 0;
      }
      setHotSpotData(hotspot);
      setInitialized(true);
    }
    if (isHotSpotInitialized) {
      let parsedCurrenTime: number = parseInt(String(currentTime), 10);
      // only eval once per second inc, starting at the first second
      if (lastTime !== parsedCurrenTime || parsedCurrenTime === 0) {
        // increase the view count for this second
        if (hotspotData[parsedCurrenTime - 1] >= 0) {
          hotspotData[parsedCurrenTime - 1]++;
        }
        //if second is a post interval, or if it's the end of the video, then post to the server
        if (
          parsedCurrenTime === 1 ||
          parsedCurrenTime % POST_INTERVAL === 0 ||
          parsedCurrenTime === parseInt(String(duration))
        ) {
          try {
            // Ensure views are counted only for real users, exclude previews
            if (userId) {
              updateStatsHotspotMutation({
                shortUrl,
                data: {
                  hotSpotData: hotspotData.join(),
                  viewerId: userId,
                },
              });

              if (!isVideoViewed && hotspotData[parsedCurrenTime - 1]) {
                setIsVideoViewed(true);
                updateViewMutation();
              }
            }
          } catch (error) {
            console.log('Failed to update hotspot', error);
          }
        }
      }
      setLastTime(parsedCurrenTime);
    }
  };

  const determineImageDimensions = (annotation: any) => {
    const MAX_IMAGE_WIDTH = fullScreenHandle.active ? 340 : 416;
    const MAX_IMAGE_HEIGHT = 200;

    annotation.calcImageWidth =
      (annotation?.imageWidth / MAX_IMAGE_WIDTH) * playerWidth + 'px';
    annotation.calcImageHeight =
      (annotation?.imageHeight / MAX_IMAGE_HEIGHT) * (playerHeight - 65) + 'px';

    if (annotation?.calcImageWidth > playerWidth) {
      annotation.calcImageWidth = playerWidth - 20 + 'px';
    }

    if (annotation?.calcImageHeight > playerHeight) {
      annotation.calcImageHeight = playerHeight - 20 + 'px';
    }
  };

  return (
    <PlayerLayout
      small={small}
      height={height}
      width={width}
      margin={margin}
      className='player-layout'
    >
      <Figure small={small} preventWideSize={preventWideSize} maxWidth={800}>
        <FullscreenWrapper handle={fullScreenHandle}>
          <Video
            id='myVideo'
            onContextMenu={contextMenu}
            key={videoSource}
            poster={isError ? '' : thumbnail}
            preload='metadata'
            ref={videoRef}
            onClick={togglePlay}
            onPlay={onPlay}
            isFullScreen={fullScreenHandle.active}
            height={height || 'auto'}
            onTimeUpdate={handleOnTimeUpdate}
          >
            <source
              key={retryCount + playlistIndex.current + videoSource}
              src={videoSource}
              type='video/mp4'
            />
          </Video>
          {showChaptersModal && (
            <ModalChapters
              handleCloseModal={() => setShowChaptersModal(false)}
              chapters={videoChaptersData}
              onChapterClick={onChapterClick}
            />
          )}
          {videoChapters && videoChapters.length ? (
            <ChaptersPreviewButton
              onClick={() => {
                setShowChaptersModal(prev => !prev);
              }}
              isFullScreen={fullScreenHandle.active}
              showChaptersModal={showChaptersModal}
            >
              <Text>
                {videoChapters.length} {t('chapters')}
              </Text>
              <IconWrapper>
                {showChaptersModal ? (
                  <MdKeyboardArrowUp color={themes.colors.secondary[40]} />
                ) : (
                  <MdKeyboardArrowDown color={themes.colors.secondary[40]} />
                )}
              </IconWrapper>
            </ChaptersPreviewButton>
          ) : null}
          {videoAnnotations &&
            videoAnnotations?.map((annotation: AnnotationItem) => {
              let show;
              if (annotation?.customerId) show = true;
              else
                show =
                  (parseFloat(annotation?.startTime?.toString()) <
                    currentTime &&
                    parseFloat(annotation?.endTime?.toString()) >
                      currentTime) ||
                  (isVideoPaused &&
                    (!selectedAnnotationId ||
                      annotation?.annotationId === selectedAnnotationId));

              const cta = addCallToAction(annotation);

              let bottomPosition = '50px';

              if (annotation?.type === 'text') {
                return (
                  <React.Fragment key={annotation.annotationId}>
                    {annotation?.text && (
                      <AnnotationText
                        {...annotation}
                        startWithControls={hasControls}
                        isFullScreen={fullScreenHandle.active}
                        show={show}
                        bottomPosition={bottomPosition}
                        key={annotation?.annotationId + 'text'}
                        onClick={() => {
                          if (cta.href) {
                            openNewTab(cta.href, cta.target);
                          }
                        }}
                        bottom={annotation?.text2 && '40px'}
                        padding={isAutomotive && '8px'}
                      >
                        {annotation?.isLinkEnabled && (
                          <LinkWrapper>
                            <IoMdLink
                              color={
                                annotation?.textColor ||
                                themes.colors.black[100]
                              }
                              size='14px'
                            />
                          </LinkWrapper>
                        )}

                        <div>{annotation?.text}</div>
                      </AnnotationText>
                    )}
                    {annotation?.text2 && (
                      <AnnotationText
                        {...annotation}
                        startWithControls={hasControls}
                        isFullScreen={fullScreenHandle.active}
                        show={show}
                        bottomPosition={bottomPosition}
                        key={annotation?.annotationId + 'text2'}
                        padding={isAutomotive && '8px'}
                        onClick={() => {
                          if (cta.href) {
                            openNewTab(cta.href, cta.target);
                          }
                        }}
                      >
                        {annotation?.isLinkEnabled && (
                          <LinkWrapper>
                            <IoMdLink
                              color={
                                annotation?.textColor ||
                                themes.colors.black[100]
                              }
                              size='14px'
                            />
                          </LinkWrapper>
                        )}

                        <div>{annotation?.text2}</div>
                      </AnnotationText>
                    )}
                  </React.Fragment>
                );
              }

              if (annotation?.type === 'image') {
                determineImageDimensions(annotation);

                // set image opacity to 0 if annotation is hidden
                let imageOpacity =
                  annotation?.imageOpacity !== '0%'
                    ? annotation?.imageOpacity
                    : '100%';
                if (!show) {
                  imageOpacity = '0%';
                }

                return (
                  <AnnotationImageWrapper
                    hasControls={hasControls}
                    key={annotation.annotationId}
                  >
                    <AnnotationImage
                      {...annotation}
                      isFullScreen={fullScreenHandle.active}
                      show={show}
                      src={annotation?.imageUrl}
                      key={annotation?.annotationId}
                      bottomPosition={bottomPosition}
                      imageOpacity={imageOpacity}
                      onClick={() => {
                        if (cta.href) {
                          openNewTab(cta.href, cta.target);
                        }
                      }}
                      startWithControls={hasControls}
                    />
                  </AnnotationImageWrapper>
                );
              }

              return null;
            })}

          {isError && (
            <Overlay>
              <LoadingIndicator
                height={'100%'}
                isLoading={isError}
                text={t('processing...')}
              />
            </Overlay>
          )}
          {/*Customizable video controls */}
          <Controls
            ref={controlsRef}
            hex={playerBgColor}
            small={small}
            isVisible={hasControls}
          >
            <VideoControls
              isVideoPaused={isVideoPaused}
              togglePlay={togglePlay}
              isVideoMuted={isVideoMuted}
              handleMute={handleMute}
              handleFullScreen={toggleFullScreen}
              setPlayBack={setPlayBack}
              setTranslate={setTranslate}
              duration={duration}
              currentTime={currentTime}
              handleVolumeControl={handleVolumeControl}
              volumeRange={volumeRange}
              playerIconsColor={playerIcColor}
              small={small}
              scrub={scrub}
              playerBackgroundColor={playerBgColor}
              captions={captions}
              isVisible={hasControls}
              videoRef={videoRef}
              videoChaptersData={videoChaptersData}
              hidePoweredBy={hidePoweredBy}
              captionsEnabled={captionsEnabled}
              initialCaptionsLoaded={initialCaptionsLoaded}
            />
          </Controls>
        </FullscreenWrapper>
        {/* On screen button */}
        <OnScreenPlayButton
          playerBackgroundColor={playerBgColor}
          playButtonPosition={playBtnPosition}
          isVideoPaused={isVideoPaused}
          isVideoFinished={isVideoFinished}
          togglePlay={togglePlay}
          playerIconsColor={playerIcColor}
          interactiveCTAs={interactiveCTAs}
          showContactInfoModal={showContactInfoModal}
          setShowCtaVideoModal={setShowCtaVideoModal}
          isVideoPlayed={!!isVideoPlayedRef.current}
        />

        {showCtaVideoModal !== '' && (
          <ModalVideoCTA
            handleCloseModal={() => setShowCtaVideoModal('')}
            videoSource={showCtaVideoModal}
          />
        )}
      </Figure>
    </PlayerLayout>
  );
};
